import type { RouteRecordRaw } from 'vue-router';

import { OrganizationModule, useAuthStore } from '@/entities/auth';
import { resolveChessboardTenantRedirect } from '@/features/push-navigation';
import { isPlatformHost } from '@/shared/lib';
import { isUuid } from '@/shared/lib/validators';
import { getFirstAvailableMenuTarget } from '@/widgets/main-layout';
import { parsePlainDate } from '@shared/lib/date-time';

import { marketingRoutes } from './marketing-routes';
import { platformRoutes } from './platform-routes';
import { settingsRoutes } from './settings-routes';

// Lazy-loaded Pages
const LoginPage = () => import('@/pages/auth/ui/LoginPage.vue');
const LogoutPage = () => import('@/pages/auth/ui/LogoutPage.vue');
const SetNamePage = () => import('@/pages/auth/ui/SetNamePage.vue');
const ConnectTelegramPage = () => import('@/pages/auth/ui/ConnectTelegramPage.vue');
const ConnectTelegramLinkPage = () => import('@/pages/auth/ui/ConnectTelegramLinkPage.vue');
const ConnectMaxPage = () => import('@/pages/auth/ui/ConnectMaxPage.vue');
const ConnectMaxLinkPage = () => import('@/pages/auth/ui/ConnectMaxLinkPage.vue');
const LocationSwitchPage = () => import('@/pages/location-switch').then((m) => ({ default: m.LocationSwitchPage }));
const MessengerPage = () => import('@/pages/messenger/ui/MessengerPage.vue');
const BookingChessboardPage = () => import('@/pages/booking-chessboard/ui/BookingChessboardPage.vue');
const AnalyticsPage = () => import('@/pages/analytics').then((m) => ({ default: m.AnalyticsPage }));
const AnalyticsMetricPage = () => import('@/pages/analytics').then((m) => ({ default: m.AnalyticsMetricPage }));
const NewClientRetentionReportPage = () =>
  import('@/pages/reports').then((m) => ({ default: m.NewClientRetentionReportPage }));
const RegularClientsReportPage = () => import('@/pages/reports').then((m) => ({ default: m.RegularClientsReportPage }));
const ShiftLoyaltyRegistrationsReportPage = () =>
  import('@/pages/reports').then((m) => ({ default: m.ShiftLoyaltyRegistrationsReportPage }));
const ClientsPage = () => import('@/pages/clients').then((m) => ({ default: m.ClientsPage }));
const ClientPage = () => import('@/pages/client').then((m) => ({ default: m.ClientPage }));
const PaymentsPage = () => import('@/pages/payments').then((m) => ({ default: m.PaymentsPage }));
const PaymentPage = () => import('@/pages/payment').then((m) => ({ default: m.PaymentPage }));
const InvoicesPage = () => import('@/pages/invoices').then((m) => ({ default: m.InvoicesPage }));
const InvoicePage = () => import('@/pages/invoice').then((m) => ({ default: m.InvoicePage }));
const InvoicePrintPage = () => import('@/pages/invoice-print').then((m) => ({ default: m.InvoicePrintPage }));
const InvoiceUpdPrintPage = () => import('@/pages/invoice-print').then((m) => ({ default: m.InvoiceUpdPrintPage }));
/** Счета видны без модуля: после отключения дополнения по ним принимают поступления. */
const INVOICE_PERMISSIONS = ['invoices.issue', 'invoices.confirm', 'payments.view'];
const ShiftsPage = () => import('@/pages/shifts').then((m) => ({ default: m.ShiftsPage }));
const ShiftPage = () => import('@/pages/shifts').then((m) => ({ default: m.ShiftPage }));
const ShopShowcasePage = () => import('@/pages/shop').then((m) => ({ default: m.ShowcasePage }));
const ShopProductsPage = () => import('@/pages/shop').then((m) => ({ default: m.ProductsPage }));
const ShopStockPage = () => import('@/pages/shop').then((m) => ({ default: m.StockPage }));
const ShopProductPage = () => import('@/pages/shop').then((m) => ({ default: m.ProductPage }));
const TireWorkOrdersPage = () => import('@/pages/tire-service').then((m) => ({ default: m.WorkOrdersPage }));
const TireWorkOrderPage = () => import('@/pages/tire-service').then((m) => ({ default: m.WorkOrderPage }));
const TireStoragesPage = () => import('@/pages/tire-service').then((m) => ({ default: m.StoragesPage }));
const TireStoragePage = () => import('@/pages/tire-service').then((m) => ({ default: m.StoragePage }));
// Печатные формы открываются отдельной вкладкой: грузим только их, без остальных страниц шиномонтажа.
const TireWorkOrderPrintPage = () => import('@/pages/tire-service/ui/print/WorkOrderPrintPage.vue');
const TireStorageContractPrintPage = () => import('@/pages/tire-service/ui/print/StorageContractPrintPage.vue');
const TireStorageReleasePrintPage = () => import('@/pages/tire-service/ui/print/StorageReleasePrintPage.vue');
const TireStorageLabelsPrintPage = () => import('@/pages/tire-service/ui/print/StorageLabelsPrintPage.vue');
const RadioPage = () => import('@/pages/radio/ui/RadioPage.vue');
const RadioManagePage = () => import('@/pages/radio/ui/RadioManagePage.vue');
const SopSectionsPage = () => import('@/pages/sop/ui/SopSectionsPage.vue');
const SopSectionDocumentsPage = () => import('@/pages/sop/ui/SopSectionDocumentsPage.vue');
const SopViewPage = () => import('@/pages/sop/ui/SopViewPage.vue');
const SopEditorPage = () => import('@/pages/sop/ui/SopEditorPage.vue');
const InstallPage = () => import('@/pages/install').then((m) => ({ default: m.InstallPage }));
const PushNotificationsPage = () =>
  import('@/pages/push-notifications').then((m) => ({ default: m.PushNotificationsPage }));
const PushBookingPage = () => import('@/pages/push-booking').then((m) => ({ default: m.PushBookingPage }));
const PushMessengerPage = () => import('@/pages/push-messenger').then((m) => ({ default: m.PushMessengerPage }));
const ImpersonationHandoffPage = () =>
  import('@/pages/impersonation-handoff').then((m) => ({ default: m.ImpersonationHandoffPage }));

const HomeRedirectBoundary = { render: () => null };
export const resolveHomeRedirect = (platformHost: boolean) => {
  const authStore = useAuthStore();

  if (platformHost) {
    return authStore.user?.is_super_user ? { name: 'platform.overview' } : { name: 'auth.platform-access-denied' };
  }

  if (authStore.currentOrganization) {
    const target = getFirstAvailableMenuTarget();

    return target ?? { name: 'auth.location-switch' };
  }

  return { name: 'auth.location-switch' };
};

/**
 * Определение всех маршрутов приложения
 */
export const routes: RouteRecordRaw[] = [
  // NotFound - редирект на главную
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    redirect: '/',
  },

  // Home
  {
    path: '/',
    name: 'home.index',
    component: HomeRedirectBoundary,
    beforeEnter: () => resolveHomeRedirect(isPlatformHost()),
    meta: {
      requiresOrganization: false,
    },
  },

  ...platformRoutes,

  // Public PWA installation guide
  {
    path: '/install-app',
    name: 'install.index',
    component: InstallPage,
    meta: {
      sidebar: false,
      requiresAuth: false,
      requiresOrganization: false,
    },
  },

  // User/device-scoped Web Push controls
  {
    path: '/push-notifications',
    name: 'push-notifications.index',
    component: PushNotificationsPage,
    meta: {
      sidebar: false,
      requiresAuth: true,
      requiresOrganization: false,
      title: 'Уведомления',
    },
  },

  // Validates a privacy-safe push payload, switches tenant and opens the day.
  {
    path: '/push/booking/:date',
    name: 'push.booking',
    component: PushBookingPage,
    meta: {
      sidebar: false,
      requiresAuth: true,
      requiresOrganization: false,
    },
  },

  // Validates a messenger push payload, switches tenant and opens the conversation.
  {
    path: '/push/messenger/:conversationId',
    name: 'push.messenger',
    component: PushMessengerPage,
    meta: {
      sidebar: false,
      requiresAuth: true,
      requiresOrganization: false,
    },
  },

  // Auth
  {
    path: '/auth/impersonation-handoff',
    name: 'auth.impersonation-handoff',
    component: ImpersonationHandoffPage,
    meta: {
      sidebar: false,
      requiresAuth: false,
      requiresOrganization: false,
    },
  },
  {
    path: '/auth/login',
    name: 'auth.login',
    component: LoginPage,
    meta: {
      sidebar: false,
      requiresAuth: false,
      requiresOrganization: false,
      onlyGuest: true,
    },
  },
  {
    path: '/auth/logout',
    name: 'auth.logout',
    component: LogoutPage,
    meta: {
      sidebar: false,
      requiresAuth: true,
      requiresOrganization: false,
    },
  },
  {
    path: '/auth/set-name',
    name: 'auth.set-name',
    component: SetNamePage,
    meta: {
      sidebar: false,
      requiresAuth: true,
      requiresOrganization: false,
    },
  },
  {
    path: '/auth/connect/telegram',
    name: 'auth.connect-telegram',
    component: ConnectTelegramPage,
    meta: {
      sidebar: false,
      requiresAuth: true,
      requiresOrganization: false,
    },
  },
  {
    path: '/auth/connect/telegram/link',
    name: 'auth.connect-telegram-link',
    component: ConnectTelegramLinkPage,
    meta: {
      sidebar: false,
      requiresAuth: true,
      requiresOrganization: false,
    },
  },
  {
    path: '/auth/connect/max',
    name: 'auth.connect-max',
    component: ConnectMaxPage,
    meta: {
      sidebar: false,
      requiresAuth: true,
      requiresOrganization: false,
    },
  },
  {
    path: '/auth/connect/max/link',
    name: 'auth.connect-max-link',
    component: ConnectMaxLinkPage,
    meta: {
      sidebar: false,
      requiresAuth: true,
      requiresOrganization: false,
    },
  },
  {
    path: '/auth/location-switch',
    name: 'auth.location-switch',
    component: LocationSwitchPage,
    meta: {
      sidebar: false,
      requiresAuth: true,
      requiresOrganization: false,
    },
  },

  // Messenger
  {
    path: '/messenger',
    name: 'messenger.index',
    component: MessengerPage,
    meta: {
      sidebar: true,
      modules: [OrganizationModule.Messenger],
      permissions: ['messenger.view'],
    },
  },

  // Booking Chessboard
  {
    path: '/booking-chessboard/:date/:bookingId?',
    name: 'booking.chessboard',
    component: BookingChessboardPage,
    props(route) {
      return {
        selectedDate: parsePlainDate(route.params.date),
        bookingId: isUuid(route.params.bookingId) ? route.params.bookingId : null,
      };
    },
    beforeEnter: (to) => {
      const authStore = useAuthStore();

      return resolveChessboardTenantRedirect(to, {
        organizationId: authStore.currentOrganization?.id ?? null,
        locationId: authStore.currentLocation?.id ?? null,
      });
    },
    meta: {
      sidebar: true,
      modules: [OrganizationModule.Booking],
    },
  },

  // Analytics overview
  {
    path: '/analytics',
    name: 'analytics.index',
    component: AnalyticsPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Отчёты',
      modules: [OrganizationModule.Analytics],
      permissions: ['analytics.view'],
    },
  },
  {
    path: '/analytics/:metric',
    name: 'analytics.metric',
    component: AnalyticsMetricPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Отчёты',
      modules: [OrganizationModule.Analytics],
      permissions: ['analytics.view'],
    },
  },

  // Reports
  {
    path: '/reports/loyalty-registrations',
    name: 'reports.loyalty-registrations',
    component: ShiftLoyaltyRegistrationsReportPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Отчёты',
      permissions: ['reports.view'],
    },
  },

  {
    path: '/reports/new-client-retention',
    name: 'reports.new-client-retention',
    component: NewClientRetentionReportPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Отчёты',
      permissions: ['analytics.view'],
    },
  },

  {
    path: '/reports/regular-clients',
    name: 'reports.regular-clients',
    component: RegularClientsReportPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Отчёты',
      permissions: ['analytics.view'],
    },
  },

  // Clients (GraphQL)
  {
    path: '/clients',
    name: 'clients.index',
    component: ClientsPage,
    meta: {
      requiresAuth: true,
      title: 'Клиенты',
      modules: [OrganizationModule.Clients],
      permissions: ['clients.detail'],
    },
  },
  {
    path: '/clients/:id',
    name: 'clients.show',
    component: ClientPage,
    meta: {
      requiresAuth: true,
      modules: [OrganizationModule.Clients],
      permissions: ['clients.detail'],
      title: 'Клиент',
    },
  },

  // Orders
  {
    path: '/payments',
    name: 'payments.index',
    component: PaymentsPage,
    meta: {
      requiresAuth: true,
      requiresOrganization: true,
      permissions: ['payments.view', 'payments.accept', 'fiscalization.manage'],
      title: 'Платежи',
    },
  },
  // Карточка одна: по платежу или по заказу без платежа.
  {
    path: '/payments/orders/:orderId',
    name: 'payments.order',
    component: PaymentPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Платёж',
      permissions: ['payments.view'],
    },
  },
  {
    path: '/payments/:id',
    name: 'payments.show',
    component: PaymentPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Платёж',
      permissions: ['payments.view', 'payments.accept', 'fiscalization.manage'],
    },
  },
  {
    path: '/payments/operations/:id',
    redirect: (to) => ({ name: 'payments.show', params: { id: to.params.id } }),
  },
  {
    path: '/invoices',
    name: 'invoices.index',
    component: InvoicesPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Счета',
      permissions: INVOICE_PERMISSIONS,
    },
  },
  {
    path: '/invoices/:id',
    name: 'invoices.show',
    component: InvoicePage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Счёт',
      permissions: INVOICE_PERMISSIONS,
    },
  },
  {
    path: '/shifts',
    name: 'shifts.index',
    component: ShiftsPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Смены',
      modules: [OrganizationModule.Orders],
      permissions: ['payments.view'],
    },
  },
  {
    path: '/shifts/:id',
    name: 'shifts.show',
    component: ShiftPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Смена',
      modules: [OrganizationModule.Orders],
      permissions: ['payments.view'],
    },
  },

  // Шиномонтаж
  {
    path: '/tire-service/work-orders',
    name: 'tire-service.work-orders.index',
    component: TireWorkOrdersPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Заказ-наряды',
      modules: [OrganizationModule.TireService],
      permissions: ['tire_service.view'],
    },
  },
  {
    path: '/tire-service/work-orders/new',
    name: 'tire-service.work-orders.new',
    component: TireWorkOrderPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Заказ-наряд',
      modules: [OrganizationModule.TireService],
      permissions: ['tire_service.manage'],
    },
  },
  {
    path: '/tire-service/work-orders/:id',
    name: 'tire-service.work-orders.show',
    component: TireWorkOrderPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Заказ-наряд',
      modules: [OrganizationModule.TireService],
      permissions: ['tire_service.view'],
    },
  },
  {
    path: '/tire-service/storages',
    name: 'tire-service.storages.index',
    component: TireStoragesPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Хранение',
      modules: [OrganizationModule.TireStorage],
      permissions: ['tire_service.view'],
    },
  },
  {
    path: '/tire-service/storages/:id',
    name: 'tire-service.storages.show',
    component: TireStoragePage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Хранение',
      modules: [OrganizationModule.TireStorage],
      permissions: ['tire_service.view'],
    },
  },
  // Печатные формы открываются в новой вкладке без каркаса админки.
  {
    path: '/tire-service/work-orders/:id/print',
    name: 'tire-service.work-order-print',
    component: TireWorkOrderPrintPage,
    meta: {
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Заказ-наряд',
      modules: [OrganizationModule.TireService],
      permissions: ['tire_service.view'],
    },
  },
  {
    path: '/tire-service/storages/:id/contract',
    name: 'tire-service.storage-contract-print',
    component: TireStorageContractPrintPage,
    meta: {
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Договор хранения',
      modules: [OrganizationModule.TireStorage],
      permissions: ['tire_service.view'],
    },
  },
  {
    path: '/tire-service/storages/:id/releases/:releaseId/act',
    name: 'tire-service.storage-release-print',
    component: TireStorageReleasePrintPage,
    meta: {
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Акт выдачи',
      modules: [OrganizationModule.TireStorage],
      permissions: ['tire_service.view'],
    },
  },
  {
    path: '/invoices/:id/print',
    name: 'invoices.print',
    component: InvoicePrintPage,
    meta: { requiresAuth: true, requiresOrganization: true, title: 'Счёт на оплату', permissions: INVOICE_PERMISSIONS },
  },
  {
    path: '/invoices/:id/upd',
    name: 'invoices.upd-print',
    component: InvoiceUpdPrintPage,
    meta: { requiresAuth: true, requiresOrganization: true, title: 'УПД', permissions: INVOICE_PERMISSIONS },
  },
  {
    path: '/tire-service/storages/:id/labels',
    name: 'tire-service.storage-labels-print',
    component: TireStorageLabelsPrintPage,
    meta: {
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Наклейки хранения',
      modules: [OrganizationModule.TireStorage],
      permissions: ['tire_service.view'],
    },
  },

  // Shop
  {
    path: '/shop',
    name: 'shop.showcase',
    component: ShopShowcasePage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Витрина',
      modules: [OrganizationModule.Shop],
      permissions: ['payments.accept'],
    },
  },
  {
    path: '/shop/stock',
    name: 'shop.stock',
    component: ShopStockPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Склад',
      modules: [OrganizationModule.Shop],
      permissions: ['shop.receive'],
      // Карточка товара и операция открываются поверх того же списка.
      keepScrollOnQueryChange: true,
    },
  },
  {
    path: '/shop/products',
    name: 'shop.products.index',
    component: ShopProductsPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Каталог',
      modules: [OrganizationModule.Shop],
      permissions: ['shop.view'],
    },
  },
  {
    path: '/shop/products/:id',
    name: 'shop.products.show',
    component: ShopProductPage,
    meta: {
      sidebar: true,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Товар',
      modules: [OrganizationModule.Shop],
      permissions: ['shop.view'],
    },
  },

  ...settingsRoutes,

  // Радио
  {
    path: '/radio',
    name: 'radio.index',
    component: RadioPage,
    meta: {
      requiresAuth: true,
      title: 'Радио',
      modules: [OrganizationModule.Radio],
    },
  },
  {
    path: '/radio/manage',
    name: 'radio.manage',
    component: RadioManagePage,
    meta: {
      requiresAuth: true,
      title: 'Управление',
      modules: [OrganizationModule.Radio],
      permissions: ['radio.manage'],
    },
  },
  // Переходный redirect: прежний адрес раздела в закладках устройств в залах
  {
    path: '/soundflow',
    redirect: { name: 'radio.index' },
  },
  {
    path: '/soundflow/manage',
    redirect: { name: 'radio.manage' },
  },

  // SOP
  {
    path: '/sop',
    name: 'sop.sections',
    component: SopSectionsPage,
    meta: {
      requiresAuth: true,
      title: 'Разделы',
      modules: [OrganizationModule.Sop],
      permissions: ['sop.view'],
    },
  },
  {
    path: '/sop/section/:sectionId',
    name: 'sop.section',
    component: SopSectionDocumentsPage,
    meta: {
      requiresAuth: true,
      title: 'Документы раздела',
      modules: [OrganizationModule.Sop],
      permissions: ['sop.view'],
    },
  },
  {
    path: '/sop/create',
    name: 'sop.create',
    component: SopEditorPage,
    meta: {
      requiresAuth: true,
      title: 'Создать регламент',
      modules: [OrganizationModule.Sop],
      permissions: ['sop.manage'],
    },
  },
  {
    path: '/sop/:id',
    name: 'sop.view',
    component: SopViewPage,
    meta: {
      requiresAuth: true,
      title: 'Просмотр регламента',
      modules: [OrganizationModule.Sop],
      permissions: ['sop.view'],
    },
  },
  {
    path: '/sop/:id/edit',
    name: 'sop.edit',
    component: SopEditorPage,
    meta: {
      requiresAuth: true,
      title: 'Редактировать регламент',
      modules: [OrganizationModule.Sop],
      permissions: ['sop.manage'],
    },
  },

  ...marketingRoutes,
];
